import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faUserGraduate, faHourglassHalf, faTriangleExclamation, faLock } from '@fortawesome/free-solid-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { countryFromTz, ianaOf, isIana, cityOf } from '../Admin/Studentzone';
import { LEBANON_TZ, nextLebanonSlot, utcLabel } from '../utils/lebanonTime';
import { makeShortNames } from '../utils/studentNames';
import './PaidAvailability.css';

const API = process.env.REACT_APP_API_BASE_URL;

/* ------------------------------------------------------------------
 * Slots are stored as LEBANON wall-clock times (Slot number -> {h, m}).
 * Same as the backend SLOT_MAP used in getavailabilitiesnewversion.
 * ------------------------------------------------------------------ */
const SLOT_TIME_LEBANON = {
  8: { h: 8, m: 0 },
  9: { h: 9, m: 0 },
  10: { h: 10, m: 0 },
  11: { h: 11, m: 0 },
  12: { h: 12, m: 0 },
  13: { h: 13, m: 0 },
  14: { h: 14, m: 30 },
  15: { h: 15, m: 30 },
  16: { h: 16, m: 30 },
  17: { h: 17, m: 30 },
  18: { h: 18, m: 30 },
  19: { h: 19, m: 30 },
  20: { h: 20, m: 30 },
  21: { h: 21, m: 30 },
  22: { h: 22, m: 30 },
};
const SLOTS = Object.keys(SLOT_TIME_LEBANON).map(Number);

/* Lebanon time → the viewer's local time (Lebanon has summer and winter time, so no fixed +3) */
function formatSlotInLocalTimezone(h, m) {
  return nextLebanonSlot(1, h, m).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

/* A student's flag + their own time for one weekly slot (shown on hover) */
const studentZone = (student, day, slot) => {
  const tz = ianaOf(student?.Timezone);
  if (!tz || !isIana(tz)) return null;
  const at = nextLebanonSlot(day, SLOT_TIME_LEBANON[slot].h, SLOT_TIME_LEBANON[slot].m);
  const theirs = at.toLocaleString('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit' });
  const lebanon = at.toLocaleString('en-US', { timeZone: LEBANON_TZ, weekday: 'short', hour: 'numeric', minute: '2-digit' });
  const inLebanon = tz === LEBANON_TZ || utcLabel(tz, at) === utcLabel(LEBANON_TZ, at);
  const flag = countryFromTz(tz);
  const city = cityOf(tz);
  return {
    flag,
    tip: inLebanon
      ? `${city} · ${theirs} (same as Lebanon)`
      : `${city} · ${theirs} their time (${utcLabel(tz, at)}) · Lebanon ${lebanon}`,
  };
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const todayIndex = () => ((new Date().getDay() + 6) % 7) + 1; // 1 = Monday


const PaidAvailability = ({ userData }) => {
  const [students, setStudents] = useState([]);
  // "David Nas." / "David Noh." when two students would both be "David N."
  const shortName = useMemo(() => makeShortNames(students.map((x) => ({ first: x.FirstName, last: x.LastName }))), [students]);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [toast, setToast] = useState(null);
  const [day, setDay] = useState(todayIndex()); // phones only
  const [acceptDemos, setAcceptDemos] = useState(true); // new demo classes on / off
  const [savingDemos, setSavingDemos] = useState(false);
  const toastTimer = useRef(null);

  const teacherId = userData?.id;

  const labels = useMemo(() => {
    const map = {};
    SLOTS.forEach((s) => {
      map[s] = formatSlotInLocalTimezone(SLOT_TIME_LEBANON[s].h, SLOT_TIME_LEBANON[s].m);
    });
    return map;
  }, []);

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/paidavailability/${teacherId}`);
        if (cancelled) return;
        const initial = {};
        (res.data?.availability || []).forEach((s) => {
          if (s.Checkteacher === 1) initial[`${s.DayoftheWeek}-${s.Slot}`] = true;
        });
        setStudents(res.data?.students || []);
        setChecked(initial);
        setAcceptDemos(res.data?.acceptDemos !== false);
      } catch (err) {
        console.error('Error fetching availability:', err);
        if (!cancelled) setError('We couldn’t load your availability.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, reload]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = (message, tone) => {
    clearTimeout(toastTimer.current);
    setToast({ message, tone, id: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };

  const studentAt = (d, s) => students.find((x) => x.DayoftheWeek === d && x.Slot === s);

  // ---------- Toggle one slot (same endpoint + same payload) ----------
  const toggle = async (d, s) => {
    const key = `${d}-${s}`;
    if (saving[key]) return;
    const next = !checked[key];
    setChecked((prev) => ({ ...prev, [key]: next }));
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/api/teacher/submitpaidavailability`, {
        teacherId,
        dayOfWeek: d,
        slot: s,
        isAvailable: next,
      });
      showToast(`${SHORT[d - 1]} ${labels[s]} ${next ? 'opened' : 'closed'}`, next ? 'on' : 'off');
    } catch (err) {
      console.error('Error submitting availability:', err);
      setChecked((prev) => ({ ...prev, [key]: !next })); // put it back
      showToast('Couldn’t save. Please try again.', 'error');
    } finally {
      setSaving((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  // ---------- New demo classes on / off (teacher here, or admin from the teacher's profile) ----------
  const toggleDemos = async () => {
    if (savingDemos) return;
    const next = !acceptDemos;
    setAcceptDemos(next);
    setSavingDemos(true);
    try {
      await axios.put(`${API}/api/teacher/paidavailability/${teacherId}/acceptdemos`, { accept: next });
      showToast(next ? 'Demo classes turned on' : 'Demo classes turned off', next ? 'on' : 'off');
    } catch (err) {
      console.error('Error saving demo setting:', err);
      setAcceptDemos(!next); // put it back
      showToast('Couldn’t save. Please try again.', 'error');
    } finally {
      setSavingDemos(false);
    }
  };

  // ---------- Counts ----------
  // open = available and not already taken by a student
  const openCount = Object.keys(checked).filter((k) => {
    if (!checked[k]) return false;
    const [d, s] = k.split('-').map(Number);
    return !students.some((x) => x.DayoftheWeek === d && x.Slot === s);
  }).length;
  const bookedCount = students.length;
  const perDay = (d) => ({
    open: SLOTS.filter((s) => checked[`${d}-${s}`] && !studentAt(d, s)).length,
    booked: SLOTS.filter((s) => studentAt(d, s)).length,
  });

  /* One cell, used in the grid and in the phone list (a plain function, so buttons keep focus) */
  const renderCell = (d, s, variant) => {
    const key = `${d}-${s}`;
    const student = studentAt(d, s);
    const time = labels[s];

    if (student) {
      const pending = student.SubscriptionStatus === 'pending'; // reserved, not paid yet
      const ending = !pending && student.sessionsremainig === 0;
      const zone = studentZone(student, d, s);
      const until = pending && student.PauseUntil
        ? new Date(student.PauseUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
        : '';
      return (
        <div
          className={`rmpa-cell is-booked ${pending ? 'is-pending' : ending ? 'is-ending' : ''} rmpa-cell--${variant}`}
          title={[`${student.FirstName || ''} ${student.LastName || ''}`.trim(), pending ? `Reserved, not paid yet${until ? ` (until ${until})` : ''}` : '', zone?.tip].filter(Boolean).join('\n')}
        >
          <FontAwesomeIcon icon={pending ? faLock : ending ? faHourglassHalf : faUserGraduate} className="rmpa-cell-icon" />
          <span className="rmpa-cell-text">
            <strong>
              {zone?.flag && <span className={`fi fi-${zone.flag} rmpa-flag`} aria-label={zone.tip} role="img" />}
              {shortName(student.FirstName, student.LastName)}
            </strong>
            <span>{pending ? `Reserved${until ? ` · ${until}` : ''}` : ending ? 'Renewal pending' : variant === 'row' ? 'Booked' : time}</span>
          </span>
          {variant === 'row' && <span className="rmpa-row-time">{time}</span>}
        </div>
      );
    }

    const on = !!checked[key];
    return (
      <button
        type="button"
        className={`rmpa-cell ${on ? 'is-on' : 'is-off'} ${saving[key] ? 'is-saving' : ''} rmpa-cell--${variant}`}
        onClick={() => toggle(d, s)}
        aria-pressed={on}
        aria-label={`${DAYS[d - 1]} ${time}: ${on ? 'available, tap to close' : 'closed, tap to open'}`}
      >
        {variant === 'row' ? (
          <>
            <span className="rmpa-row-time">{time}</span>
            <span className="rmpa-row-state">{on ? 'Available' : 'Closed'}</span>
            <span className="rmpa-switch" aria-hidden="true">
              <i />
            </span>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={on ? faCheck : faPlus} className="rmpa-cell-icon" />
            <span className="rmpa-cell-time">{time}</span>
          </>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="rmpa" aria-busy="true">
        <div className="rmpa-skel rmpa-skel--top" />
        <div className="rmpa-skel rmpa-skel--grid" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmpa">
        <div className="rmpa-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmpa-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rmpa">
      {/* ---------- Intro + counts ---------- */}
      <div className="rmpa-top">
        <div>
          <h2>Your weekly availability</h2>
          <p>Tap a time to open it for {acceptDemos ? 'demo and paid classes' : 'paid classes'}. It repeats every week.</p>
        </div>
        <ul className="rmpa-stats">
          <li className="is-on">
            <strong>{openCount}</strong> open
          </li>
          <li className="is-booked">
            <strong>{bookedCount}</strong> booked
          </li>
        </ul>
      </div>

      <div className={`rmpa-demos ${acceptDemos ? 'is-on' : 'is-off'}`}>
        <div className="rmpa-demos-text">
          <strong>Accept new demo classes</strong>
          <span>
            {acceptDemos
              ? 'Open times are shown to parents booking a free trial.'
              : 'Off: these times are hidden from the free trial form. Paid classes are not affected.'}
          </span>
        </div>
        <button
          type="button"
          className="rmpa-demos-btn"
          onClick={toggleDemos}
          disabled={savingDemos}
          aria-pressed={acceptDemos}
          aria-label={`Accept new demo classes: ${acceptDemos ? 'on' : 'off'}`}
        >
          <span>{acceptDemos ? 'Yes' : 'No'}</span>
          <span className="rmpa-switch" aria-hidden="true">
            <i />
          </span>
        </button>
      </div>

      <ul className="rmpa-legend">
        <li className="is-on">
          <i /> Available
        </li>
        <li className="is-booked">
          <i /> Booked student
        </li>
        <li className="is-ending">
          <i /> Package ended, renewal pending
        </li>
        <li className="is-pending">
          <i /> Reserved, not paid yet
        </li>
        <li className="is-off">
          <i /> Closed
        </li>
      </ul>

      {/* ---------- Grid (tablet / desktop) ---------- */}
      <div className="rmpa-wrap">
        <div className="rmpa-grid">
          <div className="rmpa-corner" />
          {SHORT.map((label, i) => {
            const c = perDay(i + 1);
            return (
              <div key={label} className={`rmpa-dayhead ${todayIndex() === i + 1 ? 'is-today' : ''}`}>
                <strong>{label}</strong>
                <span>
                  {c.open} open{c.booked ? ` · ${c.booked} booked` : ''}
                </span>
              </div>
            );
          })}

          {SLOTS.map((s) => (
            <React.Fragment key={s}>
              <div className="rmpa-time">{labels[s]}</div>
              {SHORT.map((_, i) => (
                <React.Fragment key={`${i + 1}-${s}`}>{renderCell(i + 1, s, 'grid')}</React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ---------- Day list (phones) ---------- */}
      <div className="rmpa-mobile">
        <div className="rmpa-days" role="tablist" aria-label="Day">
          {SHORT.map((label, i) => {
            const d = i + 1;
            const c = perDay(d);
            return (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={day === d}
                className={`${day === d ? 'is-on' : ''} ${todayIndex() === d ? 'is-today' : ''}`}
                onClick={() => setDay(d)}
              >
                <span>{label}</span>
                <strong>{c.open + c.booked}</strong>
              </button>
            );
          })}
        </div>
        <ul className="rmpa-list">
          {SLOTS.map((s) => (
            <li key={s}>
              {renderCell(day, s, 'row')}
            </li>
          ))}
        </ul>
      </div>

      {toast && (
        <div key={toast.id} className={`rmpa-toast is-${toast.tone}`} role="status" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default PaidAvailability;